import { useMemo, useState } from 'react';
import { Check, Folder, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Dialog, DialogContent, DialogTitle } from 'components/ui/dialog';
import { Skeleton } from 'components/ui/skeleton';
import { cn } from 'lib/utils';

import { useGetFolderList } from 'features/chat/hooks/use-kb';

import type { IFolder } from 'features/chat/agent-chat-module/types/kb.service.type';
import { getPickerTheme } from 'pages/agents/utils/agent-config-picker-theme';

type AgentConfigAddKBModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectKey: string;
  currentFolderIds: string[];
  onAddKB: (id: string) => void;
  onRemoveKB: (id: string) => void;
};

export function AgentConfigAddKBModal({
  open,
  onOpenChange,
  projectKey,
  currentFolderIds,
  onAddKB,
  onRemoveKB,
}: AgentConfigAddKBModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const ui = getPickerTheme(theme === 'dark');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: folderListData, isLoading } = useGetFolderList(projectKey, open);
  const folders = useMemo(() => folderListData?.data?.folders || [], [folderListData]);

  const filteredFolders = useMemo(
    () =>
      folders.filter((folder: IFolder) =>
        (folder.Name || '').toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [folders, searchQuery]
  );

  const selectedFolders = folders.filter((folder: IFolder) => currentFolderIds.includes(folder._id));
  const selectedCount = selectedFolders.length;

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSearchQuery('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        hideClose
        className={cn(
          'flex h-[min(92dvh,720px)] w-[calc(100vw-2rem)] max-w-[640px] flex-col gap-0 overflow-hidden rounded-3xl p-0 shadow-2xl',
          ui.dialog
        )}
      >
        <DialogTitle className="sr-only">
          {t('ADD_KNOWLEDGE_BASE_FOLDER') || 'Add Knowledge Base Folder'}
        </DialogTitle>

        <div className={cn('relative shrink-0 px-6 pb-5 pt-6', ui.header)}>
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cn('absolute right-4 top-4 rounded-full p-1.5 transition-colors', ui.close)}
            aria-label={t('CLOSE', 'Close')}
          >
            <X className="h-4 w-4" />
          </button>

          <p className={cn('pr-8 font-sans text-[1.65rem] font-bold leading-tight', ui.title)}>
            {t('ADD_KNOWLEDGE_BASE_FOLDER') || 'Add Knowledge Base Folder'}
          </p>
          <p className={cn('mt-2 pr-8 font-sans text-sm leading-5', ui.subtitle)}>
            {t('BROWSE_AND_SELECT_EXISTING_FOLDERS') ||
              'Browse and select existing folders to add to your agent.'}
          </p>

          <div className="relative mt-5">
            <Search
              className={cn(
                'pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2',
                ui.searchIcon
              )}
            />
            <input
              type="search"
              placeholder={t('SEARCH_FOLDERS') || 'Search folders...'}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className={cn(
                'h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition-colors',
                ui.search
              )}
            />
          </div>
        </div>

        {selectedCount > 0 && (
          <div className={cn('flex shrink-0 flex-wrap gap-2 border-b px-6 pb-4 pt-4', ui.chipWrap)}>
            {selectedFolders.map((folder: IFolder) => (
              <button
                key={folder._id}
                type="button"
                onClick={() => onRemoveKB(folder._id)}
                className={cn(
                  'inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                  ui.chip
                )}
              >
                <span className="truncate">{folder.Name || t('UNNAMED_FOLDER') || 'Unnamed Folder'}</span>
                <X className="h-3 w-3 shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className={cn('flex items-center gap-3 rounded-2xl border p-4', ui.field)}
              >
                <Skeleton className="h-11 w-11 shrink-0 rounded-2xl" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-8 w-16 rounded-full" />
              </div>
            ))
          ) : filteredFolders.length === 0 ? (
            <div className="flex h-full min-h-[220px] flex-col items-center justify-center text-center">
              <div
                className={cn(
                  'mb-3 flex h-14 w-14 items-center justify-center rounded-full',
                  ui.emptyIcon
                )}
              >
                <Folder className="h-6 w-6" />
              </div>
              <p className={cn('text-sm font-medium', ui.body)}>
                {searchQuery
                  ? t('NO_FOLDERS_FOUND_MATCHING_SEARCH') || 'No folders found matching your search'
                  : t('NO_FOLDERS_AVAILABLE') || 'No folders available'}
              </p>
            </div>
          ) : (
            filteredFolders.map((folder: IFolder) => {
              const isSelected = currentFolderIds.includes(folder._id);

              return (
                <button
                  key={folder._id}
                  type="button"
                  onClick={() => (isSelected ? onRemoveKB(folder._id) : onAddKB(folder._id))}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-2xl border px-4 py-3.5 text-left transition-colors',
                    isSelected ? ui.rowSelected : ui.rowIdle
                  )}
                >
                  <div
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl',
                      isSelected ? ui.iconSelected : ui.iconIdle
                    )}
                  >
                    <Folder className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={cn('truncate font-medium', ui.body)}>
                      {folder.Name || t('UNNAMED_FOLDER') || 'Unnamed Folder'}
                    </div>
                    <div className={cn('line-clamp-2 break-words text-sm', ui.muted)}>
                      {folder.Description || t('NO_DESCRIPTION') || 'No description'}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-3 text-xs font-semibold',
                      isSelected ? ui.pillRemove : ui.pillIdle
                    )}
                  >
                    {isSelected ? <Check className="h-3.5 w-3.5" /> : null}
                    {isSelected ? t('REMOVE') || 'Remove' : t('ADD') || 'Add'}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className={cn('shrink-0 border-t px-6 py-4', ui.footer)}>
          <p className={cn('text-sm', ui.muted)}>
            {selectedCount}{' '}
            {selectedCount !== 1
              ? t('FOLDERS_SELECTED') || 'folders selected'
              : t('FOLDER_SELECTED') || 'folder selected'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
